import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { getGHGsComplete } from '../../../static/ghg.static'
import { Table, Select, Switch, Button, Space } from 'antd'
import { useSelector } from 'react-redux'
import { selectUserDoc } from '../../../redux/features/userSlice'
import { updateCompany } from '../../../firebase/company/update'
import { catcher } from '../../../firebase/util'
import { getCurrentDate } from '../../../helper/time'
import { firstCompany } from '../../../helper/attribute'

const { Option } = Select

// Move constants outside component to prevent recreation on every render
const DATA_STORES = Object.freeze({
  calculation_type: Object.freeze(["Activity Data", "Spend Based Data"]),
  completion_status: Object.freeze(["Complete", "Partial", "Excluded", "Not relevant"]),
  justification: Object.freeze([
    "Relevant and recommended to include in future assessments",
    "Relevance currently unknown - intending to determine in future assessments",
    "Not required for the GHG Protocol Corporate Standard",
    "Not assessed as detailed data not readily available"
  ])
})

// Standards and their requirement levels for each GHG category (20 categories x 8 standards)
const STANDARDS = Object.freeze({
  "GHG Protocol Corporate Standard": [
    "Required", "Required", "Required", "Required", "Required",
    "Optional", "Optional", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional"
  ],
  "GHG Protocol Corporate Value Chain (Scope 3) Standard": [
    "NA", "NA", "NA", "NA", "NA",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required"
  ],
  "GHG Protocol Corporate Standard & GHG Protocol Corporate Value Chain (Scope 3) Standard": [
    "Required", "Required", "Required", "Required", "Required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required"
  ],
  "PPN 06/21": [
    "Required", "Required", "Required", "Required", "Required",
    "Water and Paper required", "Optional", "Optional", "Required", "Required",
    "Required", "Required", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional"
  ],
  "SECR": [
    "Required", "Required", "Required", "Required", "Required",
    "Optional", "Optional", "Optional", "Optional", "Optional",
    "Required", "Optional", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional"
  ],
  "DEFRA": [
    "Required", "Required", "Required", "Required", "Required",
    "Optional", "Optional", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional",
    "Optional", "Optional", "Optional", "Optional", "Optional"
  ],
  "SBTs": [
    "Required", "Required", "Required", "Required", "Required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required",
    "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required", "Min. boundary required"
  ]
})

const COLORS = Object.freeze({
  requirement: Object.freeze({
    "Required": "#ff4d4f",
    "Optional": "#fa8c16",
    "Min. boundary required": "#ff4d4f",
    "Water and Paper required": "#722ed1",
    "NA": "#8c8c8c",
    "": "#f0f0f0"
  }),
  completion_status: Object.freeze({
    "Complete": "#52c41a",
    "Partial": "#faad14",
    "Excluded": "#ff4d4f",
    "Not relevant": "#8c8c8c"
  })
})

// Utility functions outside component
const addTransparency = (color, opacity) => color + opacity

const Boundry = ({ userview = false }) => {
  // Use useMemo for static data that doesn't change
  const ghgCategories = useMemo(() => getGHGsComplete(), [])
  const userDoc = useSelector(selectUserDoc)

  const [tableData, setTableData] = useState([])
  const [selectedStandard, setSelectedStandard] = useState("GHG Protocol Corporate Standard")
  const [showToUser, setShowToUser] = useState(false)
  const [saving, setSaving] = useState(false)
  const company = useMemo(() => firstCompany(), [])

  // Original data for comparison
  const [originalTableData, setOriginalTableData] = useState([])
  const [originalSelectedStandard, setOriginalSelectedStandard] = useState("GHG Protocol Corporate Standard")
  const [originalShowToUser, setOriginalShowToUser] = useState(false)

  // Initialize table data with useMemo to prevent recreation
  const initialTableData = useMemo(() =>
    ghgCategories.map((category, index) => ({
      ...category,
      key: category.id || `ghg-${index}`,
      calculation_type: null,
      completion_status: null,
      justification: null
    })), [ghgCategories]
  )

  // Check if data has been modified
  const hasChanges = useMemo(() => {
    return JSON.stringify(tableData) !== JSON.stringify(originalTableData) ||
      selectedStandard !== originalSelectedStandard ||
      showToUser !== originalShowToUser
  }, [tableData, originalTableData, selectedStandard, originalSelectedStandard, showToUser, originalShowToUser])

    // Load data from company document on mount
  useEffect(() => {
    const loadCompanyData = () => {
      const defaultData = initialTableData
      const defaultStandard = "GHG Protocol Corporate Standard"
      const defaultShowToUser = false
      
      // Load saved data from company object
      const savedShowToUser = company?.boundry_data_show_to_user || defaultShowToUser
      const savedStandard = company?.boundry_selected_standard || defaultStandard
      let savedTableData = defaultData
      
      if (company?.boundry_data) {
        try {
          const parsedData = JSON.parse(company.boundry_data)
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            savedTableData = parsedData
          }
        } catch (error) {
          console.error('Error parsing saved boundry data:', error)
        }
      }
      
      // Set current state
      setTableData(savedTableData)
      setSelectedStandard(savedStandard)
      setShowToUser(savedShowToUser)
      
      // Set original data for comparison
      setOriginalTableData(JSON.parse(JSON.stringify(savedTableData)))
      setOriginalSelectedStandard(savedStandard)
      setOriginalShowToUser(savedShowToUser)
    }

    loadCompanyData()
  }, [initialTableData, company])

    // Save data to Firebase
  const handleSave = useCallback(async () => {
    if (!company?.id) return

    await catcher(async () => {
      setSaving(true)
      await updateCompany(company.id, {
        boundry_data: JSON.stringify(tableData),
        boundry_data_show_to_user: showToUser,
        boundry_selected_standard: selectedStandard,
        boundry_data_last_saved_on: getCurrentDate()
      })
      
      // Update original data after successful save
      setOriginalTableData(JSON.parse(JSON.stringify(tableData)))
      setOriginalSelectedStandard(selectedStandard)
      setOriginalShowToUser(showToUser)
      
      setSaving(false)
    }, { setLoading: setSaving })
  }, [tableData, showToUser, selectedStandard, company?.id])

  // Cancel changes and revert to original data
  const handleCancel = useCallback(() => {
    setTableData(JSON.parse(JSON.stringify(originalTableData)))
    setSelectedStandard(originalSelectedStandard)
    setShowToUser(originalShowToUser)
  }, [originalTableData, originalSelectedStandard, originalShowToUser])

  // Memoized callback for select changes
  const handleSelectChange = useCallback((recordKey, field, value) => {
    setTableData(prevData =>
      prevData.map(item =>
        item.key === recordKey
          ? { ...item, [field]: value }
          : item
      )
    )
  }, [])

  // Memoized function to get row background color
  const getRowBackgroundColor = useCallback((record) => {
    const status = record.completion_status
    if (status && COLORS.completion_status[status]) {
      return addTransparency(COLORS.completion_status[status], '20')
    }
    return 'transparent'
  }, [])

  // Memoized select renderer
  const renderSelect = useCallback((record, field, options) => {
    const value = record[field]
    const backgroundColor = field === 'completion_status' && value
      ? addTransparency(COLORS.completion_status[value], '40')
      : 'transparent'

    // In user view, just show the value as text
    if (userview) {
      return (
        <div style={{
          backgroundColor,
          padding: '4px 8px',
          borderRadius: '4px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          fontWeight: value ? 'medium' : 'normal',
          color: value ? '#000' : '#8c8c8c'
        }}>
          {value || '-'}
        </div>
      )
    }

    return (
      <div style={{
        backgroundColor,
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Select
          style={{ width: '100%', minWidth: 150 }}
          popupMatchSelectWidth={false}
          placeholder={`Select ${field.replace('_', ' ')}`}
          value={value}
          onChange={(newValue) => handleSelectChange(record.key, field, newValue)}
          allowClear
          bordered={false}
          variant="borderless"
        >
          {options.map(option => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      </div>
    )
  }, [handleSelectChange, userview])

  // Memoized requirement level renderer
  const renderRequirementLevel = useCallback((text, record, index) => {
    const standardRequirements = STANDARDS[selectedStandard] || []
    const level = standardRequirements[index] || ""

    return (
      <div style={{
        padding: '4px 8px',
        borderRadius: '4px',
        textAlign: 'center',
        fontWeight: 'bold',
        color: COLORS.requirement[level] || '#000',
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px'
      }}>
        {level || "-"}
      </div>
    )
  }, [selectedStandard])

  // Handle standard selection change
  const handleStandardChange = useCallback((value) => {
    setSelectedStandard(value)
  }, [])

  // Memoized columns definition
  const columns = useMemo(() => [
    {
      title: "Requirement Level",
      key: "requirement_level",
      width: 140,
      render: renderRequirementLevel
    },
    {
      title: "Footprint",
      dataIndex: "source",
      key: "source",
      width: 120,
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      width: 80,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text, record) => <div style={{ fontWeight: 'bold' }}>{text}</div>
    },
    {
      title: "Calculation Type",
      key: "calculation_type",
      width: 180,
      render: (text, record) => renderSelect(record, 'calculation_type', DATA_STORES.calculation_type)
    },
    {
      title: "Completion Status",
      key: "completion_status",
      width: 180,
      render: (text, record) => renderSelect(record, 'completion_status', DATA_STORES.completion_status)
    },
    {
      title: "Justification",
      key: "justification",
      width: 300,
      render: (text, record) => renderSelect(record, 'justification', DATA_STORES.justification)
    }
  ], [renderSelect, renderRequirementLevel])

  // Memoized table props
  const tableProps = useMemo(() => ({
    columns,
    dataSource: tableData,
    pagination: false,
    scroll: {
      y: 'calc(100vh - 280px)'
    },
    sticky: {
      offsetHeader: 0,
    },
    size: "middle",
    bordered: true,
    rowClassName: () => 'custom-row',
    style: {
      backgroundColor: '#fff',
      borderRadius: '6px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    }
  }), [columns, tableData])

  // Memoized onRow function
  const onRowHandler = useCallback((record) => ({
    style: {
      backgroundColor: getRowBackgroundColor(record)
    }
  }), [getRowBackgroundColor])

    // If in user view mode, only show the table if showToUser is true
  if (userview) {
    if (!showToUser) {
      return null // Don't render anything if not meant to be shown to user
    }
    
    return (
      <div style={{ padding: '20px', zoom: 0.9 }} className="boundry-table-container">
        <Table
          {...tableProps}
          onRow={onRowHandler}
        />
      </div>
    )
  }

  return (
    <div style={{ padding: '20px', zoom: 0.9 }} className="boundry-table-container">
      {/* Standards Selector */}
      <div style={{
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#fafafa',
        borderRadius: '6px',
        border: '1px solid #d9d9d9'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{
              marginBottom: '8px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#262626'
            }}>
              Select Standard:
            </div>
            <Select
              style={{ width: '100%', maxWidth: '500px' }}
              value={selectedStandard}
              onChange={handleStandardChange}
              placeholder="Select a standard"
            >
              {Object.keys(STANDARDS).map(standard => (
                <Option key={standard} value={standard}>
                  {standard}
                </Option>
              ))}
            </Select>
          </div>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '8px'
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#262626'
            }}>
              Show to User:
            </div>
            <Switch
              checked={showToUser}
              onChange={setShowToUser}
            />
          </div>
        </div>
      </div>

      {/* Save/Cancel Buttons */}
      {hasChanges && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#d46b08', fontWeight: 'medium' }}>
            You have unsaved changes
          </span>
          <Space>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={handleSave} 
              loading={saving}
            >
              Save Changes
            </Button>
          </Space>
        </div>
      )}

      <Table
        {...tableProps}
        onRow={onRowHandler}
      />
    </div>
  )
}

export default React.memo(Boundry)